import {
  EElement,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  namesEqual,
  qualify,
  type Qualified,
} from '@packages/systems/wfdl-Qualified';
import {Component, Expressions, Plugins} from '@packages/systems/wfdl';
import {MATCHING_VARIATION_ATOM_ID_PATH} from '@packages/systems/optimization/constants';
import {hasDynamoUniversalBindings} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {
  contains,
  expressionContainsSpecificElement,
  isCodeIsland,
} from '@packages/utilities/core-elements';

import {
  isLinkElement,
  isFormButtonElement,
  isNavbarLink,
  isDropdownLink,
  isNavbarBrand,
  isDynamoWrapper,
  isDynamicBoundOrConditioned,
  isList,
  isLinkBlock,
  isButton,
  isHeading,
  isParagraph,
  isTextLink,
  isDivBlock,
  isTypographicElement,
  isBlockquote,
  isRichText,
  isImageElement,
  isVideoElement,
  isYouTubeVideoElement,
  isLottie,
  isFormWrapper,
  isComponentOnlyDropTarget,
  isNavbarWrapper,
  isDropdownWrapper,
  isLightboxWrapper,
  isSliderWrapper,
  isTabsWrapper,
  isHtmlEmbed,
} from '@packages/utilities/elementTypeUtils';

const optimizationExperienceType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Optimization'),
  Component.name('OptimizationExperience')
);

const optimizationTreatmentType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Optimization'),
  Component.name('OptimizationTreatment')
);

const optimizationBaseType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Optimization'),
  Component.name('OptimizationBase')
);

export function isOptimizationExperienceElement(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, optimizationExperienceType);
}

export function isOptimizationTreatmentElement(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, optimizationTreatmentType);
}

export function isOptimizationBaseElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, optimizationBaseType);
}

export function isOptimizationVariationElement(
  element: ExpressionElement
): boolean {
  return (
    isOptimizationTreatmentElement(element) ||
    isOptimizationBaseElement(element)
  );
}

export function isOptimizationElement(element: ExpressionElement): boolean {
  return (
    isOptimizationExperienceElement(element) ||
    isOptimizationTreatmentElement(element) ||
    isOptimizationBaseElement(element)
  );
}

const isCMSElement = (el: ExpressionElement) =>
  isDynamoWrapper(el) ||
  isDynamicBoundOrConditioned(el) ||
  hasDynamoUniversalBindings(el);

function containsCMSElements(element: ExpressionElement) {
  return contains(element, isCMSElement);
}

function containsHtmlEmbed(element: ExpressionElement) {
  return contains(element, isHtmlEmbed);
}

/**
 * Checks if a user component instance contains HTML embeds (custom code)
 * anywhere in its definition, including nested components.
 * This recursively checks component internals unlike containsHtmlEmbed.
 */
export function componentContainsHtmlEmbed(
  element: ExpressionElement,
  componentMap: Component.ComponentMap
): boolean {
  return expressionContainsSpecificElement(isHtmlEmbed)(EElement(element))(
    componentMap
  );
}

/**
 * Checks if a user component instance contains CMS elements (dynamo wrappers,
 * dynamically-bound elements, universal bindings) anywhere in its definition,
 * including nested components.
 * This recursively checks component internals unlike containsCMSElements.
 */
export function componentContainsCMSElements(
  element: ExpressionElement,
  componentMap: Component.ComponentMap
): boolean {
  return expressionContainsSpecificElement(isCMSElement)(EElement(element))(
    componentMap
  );
}

/**
 * List of elements that can be wrapped in an optimization element.
 * The descendants of these elements need the ancestor that is in this list
 * to be wrapped in a variation before editing in a variation is allowed.
 * For text elements the outer rich text will take precendence over descendants
 * that are in the list.
 */
export function isValidElementForOptimization(element: ExpressionElement) {
  return (
    (isList(element) ||
      isLinkBlock(element) ||
      isButton(element) ||
      isFormButtonElement(element) ||
      isHeading(element) ||
      isParagraph(element) ||
      isTextLink(element) ||
      isNavbarLink(element) ||
      (isDivBlock(element) && isTypographicElement(element)) ||
      isBlockquote(element) ||
      isRichText(element) ||
      isImageElement(element) ||
      isVideoElement(element) ||
      isYouTubeVideoElement(element) ||
      isLottie(element) ||
      isFormWrapper(element) ||
      isComponentOnlyDropTarget(element) ||
      isNavbarWrapper(element) ||
      isDropdownWrapper(element) ||
      isLightboxWrapper(element) ||
      isSliderWrapper(element) ||
      isTabsWrapper(element)) &&
    !containsCMSElements(element) &&
    !containsHtmlEmbed(element)
  );
}

/**
 * A "whole block" is an element which descendants can not be directly optimized so
 * in optimize mode we move the selection from any of the descendants to its closest
 * "whole block" ancestor and users can safely wrap these elements in optimization variations.
 */
export function isWholeBlockElementForOptimization(element: ExpressionElement) {
  return (
    isLinkBlock(element) ||
    isList(element) ||
    isFormWrapper(element) ||
    isRichText(element) ||
    isDropdownWrapper(element) ||
    isLightboxWrapper(element) ||
    isNavbarWrapper(element) ||
    isSliderWrapper(element) ||
    isTabsWrapper(element) ||
    isButton(element) ||
    isFormButtonElement(element)
  );
}

export function getClosestValidElementForOptimization(
  tree: Expression,
  elementId: string
) {
  return (
    Expressions.closestElement(
      tree,
      elementId,
      isWholeBlockElementForOptimization
    ) ||
    Expressions.closestElement(tree, elementId, isValidElementForOptimization)
  );
}

/**
 * Check if the element is inside a whole block element for optimization (navbar, form, etc).
 */
export function isInsideOptimizationWholeBlockElement(
  element: ExpressionElement,
  tree: Expression
): boolean {
  return (
    !isWholeBlockElementForOptimization(element) &&
    Boolean(
      Expressions.closestElement(
        tree,
        element.id,
        isWholeBlockElementForOptimization
      )
    )
  );
}

export const OPTIMIZE_CLICK_TRACKING_ATTR_NAME = 'data-wf-optimize-clickable';

export const isOptimizeTrackingElement = (element: ExpressionElement) => {
  return (
    isCodeIsland(element) ||
    isLinkElement(element) ||
    isNavbarLink(element) ||
    isDropdownLink(element) ||
    isNavbarBrand(element) ||
    isFormButtonElement(element)
  );
};

export function isUnselectableVariationElement(
  element: ExpressionElement
): boolean {
  if (!isOptimizationVariationElement(element)) {
    return false;
  }
  const matchingVariationAtomId = Expressions.getElementDataAt(
    element,
    MATCHING_VARIATION_ATOM_ID_PATH,
    'Text',
    ''
  );

  if (matchingVariationAtomId) {
    return matchingVariationAtomId !== element.id;
  }

  return !isOptimizationBaseElement(element);
}

export function getOptimizationExperienceElementForBaseElement(
  element: ExpressionElement,
  expression: Expression
): ExpressionElement | null {
  if (isOptimizationBaseElement(element)) {
    const optimization = Expressions.getParent(expression, element.id);
    if (optimization && isOptimizationExperienceElement(optimization)) {
      return optimization;
    }
  }
  return null;
}

export function getOptimizationExperienceElementForBaseChild(
  element: ExpressionElement,
  expression: Expression
): ExpressionElement | null {
  const parent = Expressions.getParent(expression, element.id);
  if (parent) {
    return getOptimizationExperienceElementForBaseElement(parent, expression);
  }
  return null;
}

export function isOptimizationBaseChild(
  element: ExpressionElement,
  expression: Expression
): boolean {
  const parent = Expressions.getParent(expression, element.id);
  if (parent) {
    return isOptimizationBaseElement(parent);
  }
  return false;
}
